import { guest } from 'rimless';
import { IConnection } from 'rimless/lib/types';
import {
  Mode,
  ParentBridgeApi,
  Activity,
  SubmitData,
  SaveData,
  ChildBridgeApi,
  FileUploadRequestData,
  FileUploadResponseData,
  SpeechEvaluationRequestData,
  SpeechEvaluationResult,
} from '../types';

export interface Connection extends IConnection {
  remote: ParentBridgeApi;
}

type RemoteMethodName = keyof ParentBridgeApi;

export class ChildBridge {
  private connection: Connection | undefined;
  private activities: Activity[] = [];

  private async ensureConnection(): Promise<Connection> {
    if (!this.connection) {
      this.connection = await this.connect();
    }
    return this.connection;
  }

  private async callRemoteMethod(
    methodName: RemoteMethodName,
    errorMessage = `${methodName} is not implemented`,
  ): Promise<any> {
    const connection = await this.ensureConnection();
    const method = connection.remote[methodName];

    // if (!method) {
    //   throw new Error(errorMessage);
    // }

    return method;
  }

  private createChildApi(): ChildBridgeApi {
    return {
      getActivities: () => this.activities,
    };
  }

  async connect(): Promise<Connection> {
    const childApi = this.createChildApi();
    this.connection = (await guest.connect(childApi)) as Connection;
    return this.connection;
  }

  async getMode(): Promise<Mode> {
    const method = await this.callRemoteMethod('getMode');
    return method?.();
  }

  async registerActivities(activities: Activity[]): Promise<void> {
    this.activities = activities;
    const method = await this.callRemoteMethod('registerActivities');
    return method?.(activities);
  }

  async startStudy(): Promise<void> {
    const method = await this.callRemoteMethod('startStudy');
    return method?.();
  }

  async submit(data: SubmitData[]): Promise<void> {
    const method = await this.callRemoteMethod('submit');
    return method?.(data);
  }

  async endStudy(): Promise<void> {
    const method = await this.callRemoteMethod('endStudy');
    return method?.();
  }

  async save(key: string, data: SaveData): Promise<void> {
    const method = await this.callRemoteMethod('save');
    return method?.(key, data);
  }

  async load(key: string): Promise<any> {
    const method = await this.callRemoteMethod('load');
    return method?.(key);
  }

  async getFileUploadUrl(data: FileUploadRequestData): Promise<FileUploadResponseData> {
    const method = await this.callRemoteMethod('getFileUploadUrl');
    return method?.(data);
  }

  async requestSpeechEvaluation(data: SpeechEvaluationRequestData): Promise<string> {
    const method = await this.callRemoteMethod('requestSpeechEvaluation');
    return method?.(data);
  }

  async getSpeechEvaluationResult(sttId: string): Promise<SpeechEvaluationResult> {
    const method = await this.callRemoteMethod('getSpeechEvaluationResult');
    return method?.(sttId);
  }

  async setHeight(height: number): Promise<boolean> {
    const method = await this.callRemoteMethod('setHeight');
    return method?.(height);
  }

  close(): void {
    this.connection?.close();
  }
}
